import { useState, useEffect } from "react";
import API from "../api";
import jsPDF from "jspdf";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

export default function Invoices() {

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [form, setForm] = useState({
    id: null,
    client_name: "",
    client_email: "",
    description: "",
    amount: "",
    due_date: "",
    status: "Pending",
    payment_method: "GCash"
  });

  /* ===============================
     SAFE TOKEN HANDLER 🔥
  =============================== */
  const getHeaders = () => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.warn("⚠️ No token found");
      return { "Content-Type": "application/json" };
    }

    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`
    };
  };

  /* ===============================
     FETCH
  =============================== */
  const fetchInvoices = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API}/invoices`, {
        headers: getHeaders()
      });

      const data = await res.json();

      console.log("📡 API:", data);

      setInvoices(Array.isArray(data) ? data : data.data || []);

    } catch (err) {
      console.error(err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  /* ===============================
     CREATE / UPDATE
  =============================== */
  const handleSubmit = async () => {
    try {
      if (!form.client_name || !form.amount) {
        return alert("Fill all fields");
      }

      const url = editMode
        ? `${API}/invoices/${form.id}`
        : `${API}/invoices`;

      const method = editMode ? "PUT" : "POST";

      await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(form)
      });

      fetchInvoices();
      closeModal();

    } catch (err) {
      console.error(err);
    }
  };

  /* ===============================
     DELETE
  =============================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete invoice?")) return;

    await fetch(`${API}/invoices/${id}`, {
      method: "DELETE",
      headers: getHeaders()
    });

    setInvoices(prev => prev.filter(i => i.id !== id));
  };

  /* ===============================
     EDIT
  =============================== */
  const handleEdit = (inv) => {
    setForm(inv);
    setEditMode(true);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditMode(false);
    setForm({
      id: null,
      client_name: "",
      client_email: "",
      description: "",
      amount: "",
      due_date: "",
      status: "Pending",
      payment_method: "GCash"
    });
  };

  /* ===============================
     PDF DOWNLOAD 📄
  =============================== */
  const downloadPDF = (inv) => {
    const doc = new jsPDF();

    doc.text("INVOICE", 20, 20);
    doc.text(`Invoice #: ${inv.invoice_number}`, 20, 40);
    doc.text(`Client: ${inv.client_name}`, 20, 50);
    doc.text(`Email: ${inv.client_email}`, 20, 60);
    doc.text(`Amount: ₱${inv.amount}`, 20, 70);
    doc.text(`Status: ${inv.status}`, 20, 80);
    doc.text(`Payment: ${inv.payment_method}`, 20, 90);
    doc.text(`Due: ${inv.due_date}`, 20, 100);

    doc.save(`Invoice-${inv.invoice_number}.pdf`);
  };

  /* ===============================
     SEND EMAIL 📧
  =============================== */
  const sendEmail = async (id) => {
    await fetch(`${API}/invoices/send-email/${id}`, {
      method: "POST",
      headers: getHeaders()
    });

    alert("📧 Email sent!");
  };

  /* ===============================
     FORMAT
  =============================== */
  const format = (v) => "₱" + Number(v || 0).toLocaleString();

  /* ===============================
     KPI + CHART
  =============================== */
  const total = invoices.reduce((s,i)=>s+Number(i.amount||0),0);
  const paid = invoices.filter(i=>i.status==="Paid")
    .reduce((s,i)=>s+Number(i.amount||0),0);
  const pending = total - paid;

  const monthlyData = invoices.reduce((acc, inv) => {
    const month = new Date(inv.created_at || Date.now())
      .toLocaleString("default", { month: "short" });

    const found = acc.find(i => i.month === month);

    if (found) found.total += Number(inv.amount);
    else acc.push({ month, total: Number(inv.amount) });

    return acc;
  }, []);

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold">Invoices</h2>

        <button
          onClick={()=>setShowModal(true)}
          className="bg-green-600 text-white px-4 py-2 rounded-lg"
        >
          Create Invoice
        </button>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white p-5 border rounded">
          <h4>Total</h4>
          <p>{format(total)}</p>
        </div>
        <div className="bg-white p-5 border text-green-600 rounded">
          <h4>Paid</h4>
          <p>{format(paid)}</p>
        </div>
        <div className="bg-white p-5 border text-yellow-600 rounded">
          <h4>Pending</h4>
          <p>{format(pending)}</p>
        </div>
      </div>

      {/* CHART */}
      <div className="bg-white p-6 rounded-xl shadow">
        <h3 className="mb-4 font-bold">Monthly Revenue</h3>

        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyData}>
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="total" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* TABLE */}
      <div className="bg-white border rounded-xl overflow-x-auto">

        {loading ? (
          <div className="p-6 text-center">Loading...</div>
        ) : invoices.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No invoices yet
          </div>
        ) : (
          <table className="w-full text-sm">

            <thead className="bg-gray-100">
              <tr>
                <th className="p-4">Client</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Due</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} className="border-t">

                  <td className="p-4">
                    <div>{inv.client_name}</div>
                    <small>{inv.client_email}</small>
                  </td>

                  <td>{format(inv.amount)}</td>

                  <td>{inv.status}</td>

                  <td>{inv.due_date}</td>

                  <td className="flex gap-2 flex-wrap p-4">
                    <button onClick={()=>handleEdit(inv)} className="text-blue-600">Edit</button>
                    <button onClick={()=>handleDelete(inv.id)} className="text-red-600">Delete</button>
                    <button onClick={()=>downloadPDF(inv)} className="text-purple-600">PDF</button>
                    <button onClick={()=>sendEmail(inv.id)} className="text-indigo-600">Email</button>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center">
          <div className="bg-white p-6 rounded-xl w-96 space-y-3">

            <h3>{editMode ? "Edit" : "Create"} Invoice</h3>

            <input placeholder="Client" className="w-full border p-2"
              value={form.client_name}
              onChange={(e)=>setForm({...form,client_name:e.target.value})}
            />

            <input placeholder="Email" className="w-full border p-2"
              value={form.client_email}
              onChange={(e)=>setForm({...form,client_email:e.target.value})}
            />

            <input type="number" placeholder="Amount" className="w-full border p-2"
              value={form.amount}
              onChange={(e)=>setForm({...form,amount:e.target.value})}
            />

            <input type="date" className="w-full border p-2"
              value={form.due_date}
              onChange={(e)=>setForm({...form,due_date:e.target.value})}
            />

            <textarea placeholder="Description" className="w-full border p-2"
              value={form.description}
              onChange={(e)=>setForm({...form,description:e.target.value})}
            />

            <div className="flex justify-end gap-2">
              <button onClick={closeModal}>Cancel</button>
              <button onClick={handleSubmit} className="bg-green-600 text-white px-4 py-2 rounded">
                Save
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}